import { useState, useEffect, useCallback, useRef } from 'react';
import { Client, Message } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import {
  Notification as CustomNotification,
  NotificationInsertDTO,
  NotificationType,
} from 'types/notification';
import { getWebSocketUrl } from '../api/apiConfig';
import secureStorage from '../auth/secureStorage';

interface WebSocketMessage {
  type: string;
  content: CustomNotification | NotificationInsertDTO | string;
}

interface ConnectionStats {
  messagesSent: number;
  messagesReceived: number;
  queueSize: number;
}

const useWebSocket = (userId: number) => {
  // Use the getWebSocketUrl function from apiConfig
  const SOCKET_URL = getWebSocketUrl();

  const messageQueue = useRef<WebSocketMessage[]>([]);
  const userSpecificTopic = `/topic/notifications/${userId}`;
  const projectSpecificTopic = `/topic/project-notifications/${userId}`;
  const [stompClient, setStompClient] = useState<Client | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState<CustomNotification[]>([]);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [subscriptions, setSubscriptions] = useState<string[]>([]);
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const [connectionStats, setConnectionStats] = useState<ConnectionStats>({
    messagesSent: 0,
    messagesReceived: 0,
    queueSize: 0,
  });

  const maxConnectionAttempts = 3;
  const MAX_MESSAGES = 50;

  const handleConnect = useCallback(() => {
    console.log('WebSocket connected successfully');
    setIsConnected(true);
    setConnectionError(null);
    setConnectionAttempts(0);
  }, []);

  const handleError = useCallback((frame: any) => {
    console.error('STOMP error:', frame);
    setIsConnected(false);
    setConnectionError(
      `Connection error: ${frame.headers?.message || 'Unknown error'}`
    );
    reconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = useCallback(() => {
    console.log('WebSocket connection closed');
    setIsConnected(false);
    reconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateConnectionStats = useCallback((type: 'sent' | 'received') => {
    setConnectionStats((prev) => ({
      ...prev,
      [`messages${type === 'sent' ? 'Sent' : 'Received'}`]:
        prev[`messages${type === 'sent' ? 'Sent' : 'Received'}`] + 1,
      // Outgoing messages waiting to be flushed, not the received-messages
      // count — read from the ref so this callback doesn't depend on
      // `messages` state (see handleMessage below for why that matters).
      queueSize: messageQueue.current.length,
    }));
  }, []);

  const handleMessage = useCallback(
    (message: Message) => {
      try {
        const parsedMessage = JSON.parse(message.body);
        updateConnectionStats('received');

        // Handle both direct notification objects and wrapped messages
        const notification =
          parsedMessage.type === 'NOTIFICATION'
            ? parsedMessage.content
            : parsedMessage;

        if (
          notification.user?.id === userId &&
          Object.values(NotificationType).includes(notification.type)
        ) {
          setMessages((prevMessages) => {
            // Dedupe by the notification's own database id — it's unique per
            // event, so this only catches genuine re-deliveries of the same
            // notification (e.g. after a reconnect), not a second distinct
            // event of the same type/task/user (which type+relatedId+user
            // used to conflate, silently swallowing every status change past
            // the first one for a given task).
            const isDuplicate = prevMessages.some(
              (msg) => msg.id === notification.id
            );

            if (isDuplicate) {
              console.log('Duplicate notification detected, skipping');
              return prevMessages;
            }

            const newMessages = [...prevMessages];
            if (newMessages.length > MAX_MESSAGES) {
              newMessages.splice(0, newMessages.length - MAX_MESSAGES);
            }
            return [...newMessages, notification as CustomNotification];
          });
        }
      } catch (error) {
        console.error('Error processing WebSocket message:', error);
      }
    },
    [updateConnectionStats, userId]
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    setConnectionError(null);
  }, []);

  const reconnect = useCallback(() => {
    if (connectionAttempts < maxConnectionAttempts) {
      setConnectionAttempts((prev) => prev + 1);
      if (stompClient) {
        stompClient.deactivate();
        setTimeout(() => {
          stompClient.activate();
        }, 1000);
      }
    } else {
      setConnectionError('Max reconnection attempts reached');
    }
  }, [stompClient, connectionAttempts]);

  const addSubscription = useCallback(
    (topic: string) => {
      if (stompClient && isConnected && !subscriptions.includes(topic)) {
        stompClient.subscribe(topic, handleMessage);
        setSubscriptions((prev) => [...prev, topic]);
      }
    },
    [stompClient, isConnected, subscriptions, handleMessage]
  );

  const removeSubscription = useCallback(
    (topic: string) => {
      if (stompClient?.connected) {
        try {
          stompClient.unsubscribe(topic);
          setSubscriptions((prev) => prev.filter((t) => t !== topic));
        } catch (error) {
          console.log('Error unsubscribing:', error);
        }
      }
    },
    [stompClient]
  );

  const filterMessages = useCallback(
    (type: string) => {
      return messages.filter((msg) => msg.type === type);
    },
    [messages]
  );

  const sendMessage = useCallback(
    (message: WebSocketMessage) => {
      if (!stompClient || !isConnected) {
        console.log('Connection not ready, queueing message');
        messageQueue.current.push(message);

        // Attempt reconnection
        if (stompClient && !isConnected) {
          console.log('Attempting to reconnect...');
          stompClient.activate();
        }
        return;
      }

      try {
        console.log('Sending message:', message);
        stompClient.publish({
          destination: '/app/send-notification',
          body: JSON.stringify(message),
        });
        updateConnectionStats('sent');
      } catch (error) {
        console.error('Error sending message:', error);
        messageQueue.current.push(message);
      }
    },
    [stompClient, isConnected, updateConnectionStats]
  );

  // to handle queued messages
  useEffect(() => {
    if (isConnected && messageQueue.current.length > 0) {
      console.log('Processing queued messages:', messageQueue.current.length);
      messageQueue.current.forEach((message) => {
        sendMessage(message);
      });
      messageQueue.current = [];
    }
  }, [isConnected, sendMessage]);

  // Deps: this effect tears down and rebuilds the whole STOMP connection
  // whenever any of these change identity, so handleMessage/handleConnect/
  // handleError/handleClose must stay referentially stable across renders.
  // handleMessage used to depend (transitively, via updateConnectionStats)
  // on `messages.length`, so every incoming message changed its identity and
  // forced a full disconnect/reconnect right after being received — flapping
  // the connection on every notification and risking dropped messages sent
  // during that window. See updateConnectionStats above for the fix.
  useEffect(() => {
    let isComponentMounted = true;
    const token = secureStorage.getItem('access_token');

    if (!token || userId === 0) return;

    const client = new Client({
      webSocketFactory: () => new SockJS(SOCKET_URL),
      connectHeaders: {
        Authorization: `Bearer ${token}`,
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
    });

    client.onConnect = () => {
      if (isComponentMounted) {
        handleConnect();
        const subscriptions = [
          client.subscribe(userSpecificTopic, handleMessage),
          client.subscribe(projectSpecificTopic, handleMessage),
          client.subscribe('/user/queue/errors', handleError),
        ];
        setSubscriptions(subscriptions.map((sub) => sub.id));

        // Process any queued messages after reconnection
        if (messageQueue.current.length > 0) {
          console.log('Processing queued messages after reconnection');
          messageQueue.current.forEach((message) => {
            sendMessage(message);
          });
          messageQueue.current = [];
        }
      }
    };

    client.onStompError = handleError;
    client.onWebSocketClose = handleClose;

    console.log('Activating WebSocket connection...');
    client.activate();
    setStompClient(client);

    return () => {
      isComponentMounted = false;
      if (client.connected) {
        subscriptions.forEach((subId) => {
          try {
            client.unsubscribe(subId);
          } catch (error) {
            console.log('Cleanup unsubscribe error:', error);
          }
        });
        client.deactivate();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, handleConnect, handleError, handleClose, handleMessage]);

  const getConnectionStatus = useCallback(() => {
    if (isConnected) return 'Connected';
    if (connectionError) return `Error: ${connectionError}`;
    if (connectionAttempts > 0)
      return `Reconnecting (${connectionAttempts}/${maxConnectionAttempts})`;
    return 'Disconnected';
  }, [isConnected, connectionError, connectionAttempts, maxConnectionAttempts]);

  return {
    isConnected,
    messages,
    sendMessage,
    connectionError,
    clearMessages,
    reconnect,
    addSubscription,
    removeSubscription,
    filterMessages,
    connectionAttempts,
    connectionStats,
    getConnectionStatus,
  };
};

export default useWebSocket;
